import { useCallback } from 'react';
import { usePopout } from '~/contexts/PopoutContext';

interface UseWidgetPopoutOptions {
  widgetId: string;
  widgetComponent: React.ComponentType<any>;
  title?: string;
  width?: number;
  height?: number;
  defaultWidth?: number;
  defaultHeight?: number;
  widgetProps: Record<string, any>;
}

/**
 * Generic hook for adding popout functionality to portfolio management widgets
 *
 * @param options Configuration for the widget popout
 * @returns handlePopout function to trigger the popout
 *
 * @example
 * const handlePopout = useWidgetPopout({
 *   widgetId: 'allocation-widget-1',
 *   widgetComponent: AllocationWidget,
 *   title: 'Allocation Widget',
 *   defaultWidth: 1200,
 *   defaultHeight: 800,
 * });
 */
export const useWidgetPopout = ({
  widgetId: _widgetId,
  widgetComponent,
  title = 'Widget',
  width,
  height,
  defaultWidth = 1200,
  defaultHeight = 800,
  widgetProps,
}: UseWidgetPopoutOptions) => {
  const { openWidgetPopout } = usePopout();

  const handlePopout = useCallback(
    (triggeredWidgetId: string) => {
      // Use the provided widgetProps for the popout
      openWidgetPopout(widgetComponent, widgetProps, {
        widgetId: triggeredWidgetId,
        width: width || defaultWidth,
        height: height || defaultHeight,
        title,
      });
    },
    [
      openWidgetPopout,
      widgetComponent,
      width,
      height,
      defaultWidth,
      defaultHeight,
      title,
      widgetProps,
    ],
  );

  return handlePopout;
};
